import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, User, Search, Menu, X, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import logo from "./logo.png";
import { useCart } from "../context/CartContext";
import AuthModal from "./AuthModal";
import { Button } from "./ui/button";

export default function Header() {
	const [showAuthModal, setShowAuthModal] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const { user, logout } = useAuth();
	const { getTotalItems } = useCart();
	const navigate = useNavigate();
	const location = useLocation();

	// 🔹 Закрываем мобильное меню при любом переходе по роуту
	useEffect(() => {
		setMobileMenuOpen(false);
	}, [location.pathname]);

	const handleSearch = (e) => {
		e.preventDefault();
		if (searchQuery.trim()) {
			navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
			setSearchQuery("");
		}
	};

	const navLinks = [
		{ to: "/catalog", label: "Каталог" },
		{ to: "/about", label: "О нас" },
		{ to: "/delivery", label: "Доставка и оплата" },
		{ to: "/promotions", label: "Акции" },
		{ to: "/contacts", label: "Контакты" },
	];

	return (
		<>
			<header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-md z-50">
				<div className="max-w-7xl mx-auto pl-1 pr-4 sm:pl-2 sm:pr-6 lg:pl-3 lg:pr-8">
					<div className="flex items-center justify-between h-20 gap-4">
						{/* Logo */}
						<Link
							to="/"
							className="flex items-center gap-1 flex-shrink-0 -ml-1"
						>
							<img
								src={logo}
								alt="Blue Oyster"
								className="h-[80px] w-[80px] object-contain flex-shrink-0 mt-0 mr-3"
							/>
							<div className="hidden sm:flex flex-col justify-center -ml-2">
								<h1
									className="text-2xl font-bold text-sky-600 leading-tight whitespace-nowrap"
									style={{ fontFamily: "Cormorant Garamond, serif" }}
								>
									Blue Oyster
								</h1>
							</div>
						</Link>

						{/* Desktop Navigation */}
						<nav className="hidden lg:flex items-center space-x-6 flex-shrink-0">
							{navLinks.map((link) => (
								<Link
									key={link.to}
									to={link.to}
									className="text-sm text-gray-700 hover:text-sky-600 transition-colors font-medium whitespace-nowrap"
									data-testid={`nav-${link.label
										.toLowerCase()
										.replace(/\s+/g, "-")}`}
								>
									{link.label}
								</Link>
							))}
						</nav>

						{/* Search Bar */}
						<form
							onSubmit={handleSearch}
							className="hidden md:flex items-center flex-1 max-w-md"
						>
							<div className="relative w-full">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
								<input
									type="text"
									data-testid="search-input"
									placeholder="Поиск товаров..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
								/>
							</div>
						</form>

						{/* Actions */}
						<div className="flex items-center space-x-3 flex-shrink-0">
							<Link
								to="/favorites"
								data-testid="favorites-link"
								className="text-gray-700 hover:text-sky-600 transition-colors hidden sm:block"
							>
								<Heart className="w-5 h-5" />
							</Link>

							<Link
								to="/cart"
								data-testid="cart-link"
								className="relative text-gray-700 hover:text-sky-600 transition-colors"
							>
								<ShoppingCart className="w-5 h-5" />
								{getTotalItems() > 0 && (
									<span
										className="absolute -top-2 -right-2 bg-sky-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium"
										data-testid="cart-count"
									>
										{getTotalItems()}
									</span>
								)}
							</Link>

							{user ? (
								<div className="hidden sm:flex items-center space-x-2">
									<Link to="/profile" data-testid="profile-link">
										<Button
											variant="ghost"
											size="sm"
											className="flex items-center space-x-2 h-9"
										>
											<User className="w-4 h-4" />
											<span className="hidden lg:inline text-sm">
												{user.name}
											</span>
										</Button>
									</Link>
									<Button
										data-testid="logout-button"
										size="sm"
										variant="outline"
										onClick={logout}
										className="hidden lg:inline-flex h-9 text-sm"
									>
										Выйти
									</Button>
								</div>
							) : (
								<Button
									data-testid="login-button"
									size="sm"
									onClick={() => setShowAuthModal(true)}
									className="hidden sm:flex bg-sky-600 hover:bg-sky-700 text-white h-9 text-sm px-4"
								>
									Войти
								</Button>
							)}

							{/* Mobile Menu Button */}
							<button
								onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
								className="lg:hidden text-gray-700 hover:text-sky-600 transition-colors p-1"
								data-testid="mobile-menu-button"
							>
								{mobileMenuOpen ? (
									<X className="w-6 h-6" />
								) : (
									<Menu className="w-6 h-6" />
								)}
							</button>
						</div>
					</div>

					{/* Mobile Menu */}
					{mobileMenuOpen && (
						<div className="lg:hidden py-4 border-t" data-testid="mobile-menu">
							<form onSubmit={handleSearch} className="mb-4 md:hidden">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
									<input
										type="text"
										placeholder="Поиск товаров..."
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-sky-500"
									/>
								</div>
							</form>
							<nav className="flex flex-col space-y-2">
								{navLinks.map((link) => (
									<Link
										key={link.to}
										to={link.to}
										onClick={() => setMobileMenuOpen(false)}
										className="text-gray-700 hover:text-sky-600 py-2 px-4 rounded hover:bg-gray-50 transition-colors"
									>
										{link.label}
									</Link>
								))}
								{user ? (
									<>
										<Link
											to="/profile"
											onClick={() => setMobileMenuOpen(false)}
											className="text-gray-700 hover:text-sky-600 py-2 px-4 rounded hover:bg-gray-50"
										>
											Профиль
										</Link>
										<button
											onClick={() => {
												logout();
												setMobileMenuOpen(false);
											}}
											className="text-left text-gray-700 hover:text-sky-600 py-2 px-4 rounded hover:bg-gray-50"
										>
											Выйти
										</button>
									</>
								) : (
									<button
										onClick={() => {
											setShowAuthModal(true);
											setMobileMenuOpen(false);
										}}
										className="text-left text-sky-600 font-medium py-2 px-4 rounded hover:bg-gray-50"
									>
										Войти
									</button>
								)}
							</nav>
						</div>
					)}
				</div>
			</header>

			<AuthModal
				isOpen={showAuthModal}
				onClose={() => setShowAuthModal(false)}
			/>
		</>
	);
}
